type Id = number | string | bigint | null | undefined;

export type InternalToastPayload = {
  id: Id;
  updateType?: string | null;
  conversationType?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
  lastMessageSenderUserId?: Id;
  lastMessageSenderName?: string | null;
  channelName?: string | null;
  contactName?: string | null;
};

export type InternalToastContext = {
  currentUserId: Id;
  activeConversationId: Id;
  internalMuted: boolean;
};

export type InternalToastDecision =
  | { show: false }
  | { show: true; conversationId: number; title: string; body: string; dedupeKey: string };

function sameId(left: Id, right: Id): boolean {
  if (left === null || left === undefined || right === null || right === undefined) {
    return false;
  }
  return String(left) === String(right);
}

const INTERNAL_TYPES = new Set(["internal", "direct"]);

// Sentinel emitted by the conversation transformer when a conversation has no
// contact and no channel name (typical for direct/internal conversations). It is
// not a real display name, so the toast must not show it verbatim.
const UNKNOWN_NAME_SENTINEL = "Unknown";

function cleanName(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  return trimmed === UNKNOWN_NAME_SENTINEL ? "" : trimmed;
}

export function shouldShowInternalToast(
  payload: InternalToastPayload,
  context: InternalToastContext,
): InternalToastDecision {
  if (payload.id === null || payload.id === undefined) {
    return { show: false };
  }
  if (payload.updateType !== "message_sent") {
    return { show: false };
  }
  if (!payload.conversationType || !INTERNAL_TYPES.has(payload.conversationType)) {
    return { show: false };
  }
  if (sameId(payload.lastMessageSenderUserId, context.currentUserId)) {
    return { show: false };
  }
  if (sameId(payload.id, context.activeConversationId)) {
    return { show: false };
  }
  if (context.internalMuted) {
    return { show: false };
  }

  return {
    show: true,
    conversationId: Number(payload.id),
    title:
      cleanName(payload.channelName) ||
      cleanName(payload.lastMessageSenderName) ||
      cleanName(payload.contactName),
    body: payload.lastMessage?.trim() || "",
    dedupeKey: `${String(payload.id)}:${payload.lastMessageAt ?? ""}`,
  };
}
